import { useScienceTutorSection } from './scienceTutorContext';
import { ArrowLeft, Atom, History } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ATOMIC_MODEL_SOURCES,
  ATOMIC_MODEL_STEPS,
  ATOMIC_MODELS_TOOL_IDENTITY,
  ATOMIC_MODES,
  MODEL_QUESTIONS,
  SENIOR_ABUNDANCE_FROM_YEAR,
  type AtomicModelStep,
  type AtomicModelsMode,
  type ModelStepId,
} from './atomicModels';
import { GoldFoilExperiment } from './AtomicModelsGoldFoil';
import { IsotopesMode } from './AtomicModelsIsotopes';
import { PredictionQuestion } from './AtomicModelsQuestion';
import { StudyShell } from './StudyShell';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<AtomicModelsMode, { tab: string; eyebrow: string; title: string; description: string }> = {
  models: {
    tab: 'Atomic models',
    eyebrow: 'Evidence changes models',
    title: 'How did the model of the atom change?',
    description: 'Follow five models from Dalton to Chadwick. Predict what each experiment showed, then check.',
  },
  isotopes: {
    tab: 'Isotopes',
    eyebrow: 'Protons, neutrons and electrons',
    title: 'Build atoms, ions and isotopes',
    description: 'Choose particle counts to build a target, then calculate relative atomic mass from abundances.',
  },
};

const SCHEMATIC_SIZE = 200;
const SCHEMATIC_CENTRE = SCHEMATIC_SIZE / 2;
const ATOM_RADIUS = 80;
const EXAMPLE_SHELLS = [2, 4];
const SHELL_RADII = [42, 72];
const CLOUD_ELECTRON_POINTS = [
  [0.55, 15], [0.8, 80], [0.45, 150], [0.7, 210], [0.9, 280], [0.6, 330],
] as const;
const NUCLEUS_OFFSETS = [
  [-4, -4], [4, -4], [-4, 4], [4, 4], [0, -9], [0, 9],
] as const;

function polar(radius: number, degrees: number) {
  const radians = (degrees * Math.PI) / 180;
  return { x: SCHEMATIC_CENTRE + radius * Math.cos(radians), y: SCHEMATIC_CENTRE + radius * Math.sin(radians) };
}

function ModelSchematic({ step }: { step: AtomicModelStep }) {
  const { nucleus, electrons, neutrons } = step.schematic;
  return (
    <svg
      viewBox={`0 0 ${SCHEMATIC_SIZE} ${SCHEMATIC_SIZE}`}
      className="mx-auto w-full max-w-[14rem]"
      role="img"
      aria-label={`${step.modelName} schematic: ${step.picture}`}
    >
      {nucleus === 'none' ? (
        <circle cx={SCHEMATIC_CENTRE} cy={SCHEMATIC_CENTRE} r={ATOM_RADIUS} className="fill-stone2" />
      ) : null}
      {nucleus === 'diffuse-positive' ? (
        <g>
          <circle cx={SCHEMATIC_CENTRE} cy={SCHEMATIC_CENTRE} r={ATOM_RADIUS} className="fill-wash-coral" stroke="currentColor" />
          {[30, 110, 190, 270].map((angle) => {
            const point = polar(ATOM_RADIUS * 0.75, angle);
            return (
              <text key={angle} x={point.x} y={point.y + 4} textAnchor="middle" className="fill-brand-coral text-[13px] font-extrabold">
                +
              </text>
            );
          })}
        </g>
      ) : null}
      {electrons === 'embedded'
        ? [70, 150, 230, 310, 0].map((angle, index) => {
            const point = index === 4 ? { x: SCHEMATIC_CENTRE, y: SCHEMATIC_CENTRE } : polar(ATOM_RADIUS * 0.45, angle);
            return (
              <g key={angle}>
                <circle cx={point.x} cy={point.y} r="7" className="fill-brand-sky" />
                <text x={point.x} y={point.y + 4} textAnchor="middle" className="fill-ink text-[11px] font-extrabold">−</text>
              </g>
            );
          })
        : null}
      {electrons === 'cloud'
        ? CLOUD_ELECTRON_POINTS.map(([fraction, angle]) => {
            const point = polar(ATOM_RADIUS * fraction, angle);
            return <circle key={angle} cx={point.x} cy={point.y} r="4" className="fill-brand-sky" />;
          })
        : null}
      {electrons === 'shells'
        ? EXAMPLE_SHELLS.map((count, shellIndex) => (
            <g key={shellIndex}>
              <circle cx={SCHEMATIC_CENTRE} cy={SCHEMATIC_CENTRE} r={SHELL_RADII[shellIndex]} fill="none" stroke="currentColor" className="text-stone2" />
              {Array.from({ length: count }, (_, index) => {
                const point = polar(SHELL_RADII[shellIndex], (360 * index) / count - 90);
                return <circle key={index} cx={point.x} cy={point.y} r="4.5" className="fill-brand-sky" />;
              })}
            </g>
          ))
        : null}
      {nucleus === 'small-dense'
        ? neutrons
          ? NUCLEUS_OFFSETS.map(([dx, dy], index) => (
              <circle
                key={index}
                cx={SCHEMATIC_CENTRE + dx}
                cy={SCHEMATIC_CENTRE + dy}
                r="4"
                className={index % 2 === 0 ? 'fill-brand-coral' : 'fill-ink-soft'}
              />
            ))
          : <circle cx={SCHEMATIC_CENTRE} cy={SCHEMATIC_CENTRE} r="6" className="fill-brand-coral" />
        : null}
    </svg>
  );
}

function ModelsMode() {
  const [stepId, setStepId] = useState<ModelStepId>(ATOMIC_MODEL_STEPS[0].id);
  useScienceTutorSection('ModelsMode', { stepId });
  const step = ATOMIC_MODEL_STEPS.find((candidate) => candidate.id === stepId) ?? ATOMIC_MODEL_STEPS[0];
  const questions = MODEL_QUESTIONS.filter((question) => question.stepId === step.id);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            <History className="h-4 w-4" aria-hidden="true" /> Timeline
          </p>
          <ol className="mt-4 grid gap-2" aria-label="Atomic model timeline">
            {ATOMIC_MODEL_STEPS.map((candidate, index) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  aria-pressed={candidate.id === step.id}
                  onClick={() => setStepId(candidate.id)}
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors motion-reduce:transition-none ${
                    candidate.id === step.id
                      ? 'border-brand-sky bg-wash-sky text-ink'
                      : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
                  }`}
                >
                  <span className="block text-xs font-extrabold text-slate2">
                    {index + 1}. {candidate.yearLabel}
                  </span>
                  <span className="mt-0.5 block text-sm font-extrabold">{candidate.scientist}</span>
                </button>
              </li>
            ))}
          </ol>
        </aside>

        <article className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(220px,0.6fr)] lg:items-start">
            <div className="min-w-0">
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
                {step.yearLabel} · {step.scientist}
              </p>
              <h2 className="mt-2 text-2xl font-extrabold text-ink">{step.modelName}</h2>
              <p className="mt-3 font-medium leading-7 text-ink-soft">{step.picture}</p>
              <dl className="mt-5 grid gap-3 text-sm leading-6">
                <div className="rounded-2xl bg-wash-mint p-4">
                  <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Evidence</dt>
                  <dd className="mt-1 font-medium text-ink-soft">{step.evidence}</dd>
                </div>
                <div className="rounded-2xl bg-wash-sky p-4">
                  <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">What it explained</dt>
                  <dd className="mt-1 font-medium text-ink-soft">{step.explained}</dd>
                </div>
                <div className="rounded-2xl bg-wash-coral p-4">
                  <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">What it could not explain</dt>
                  <dd className="mt-1 font-medium text-ink-soft">{step.couldNotExplain}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-[1.5rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4">
              <ModelSchematic step={step} />
              <p className="mt-2 text-center text-xs font-bold text-slate2">
                Schematic, not to scale{step.schematic.electrons === 'shells' ? ' · example: carbon (2, 4)' : ''}
              </p>
            </div>
          </div>

          {questions.length > 0 ? (
            <section className="mt-6" aria-label="Predict with this model">
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Predict with this model</p>
              <div className="mt-3 grid gap-4 lg:grid-cols-2">
                {questions.map((question) => (
                  <PredictionQuestion key={question.id} question={question} />
                ))}
              </div>
            </section>
          ) : null}
        </article>
      </div>

      <GoldFoilExperiment />
    </div>
  );
}

export function AtomicModelsLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<AtomicModelsMode>(() => modeForYear(ATOMIC_MODES, year));
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'atomic-models', state: { mode } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8"
        data-tool-key={ATOMIC_MODELS_TOOL_IDENTITY.toolKey}
        data-tool-version={ATOMIC_MODELS_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Atom className="h-4 w-4" aria-hidden="true" /> Elements and atoms
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Atomic Models &amp; Isotopes
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              See how evidence changed the model of the atom, then build atoms, ions and isotopes.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year 9–12 · two modes
          </div>
        </div>

        <div className="mt-6 grid max-w-xl grid-cols-2 rounded-full bg-white/75 p-1" role="group" aria-label="Choose mode">
          {ATOMIC_MODES.map(({ mode: option }) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">{copy.eyebrow}</p>
            <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="shrink-0 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'models' ? <ModelsMode /> : null}
      {mode === 'isotopes' ? (
        <IsotopesMode seniorOpenByDefault={year !== null && year >= SENIOR_ABUNDANCE_FROM_YEAR} />
      ) : null}

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        Diagrams are schematics generated from the model, not to scale. History sources:{' '}
        {ATOMIC_MODEL_SOURCES.join(' · ')}. This tool uses reviewed, bounded teaching cases (pending
        teaching review). It does not save an attempt, record mastery or claim alignment to a state
        syllabus.
      </footer>
    </StudyShell>
  );
}
